import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post, put, type Me } from './api';
import { Confirm, useToast, type ConfirmSpec } from './ui';

/**
 * Messages (prototype "Messages"; CLAUDE.md "Employee messages to managers", "Message HR", "Message text
 * alerts"). StaffMessages = the desktop page for managers, admins and HR; EmployeeMessages = the phone tab.
 */

interface Item { id: number; mine: boolean; fromEmployee: boolean; who: string; at: string; body: string; status: { text: string; cls: string; retry?: boolean } | null }
export interface Unread { staff: number; hr: number; mine: number }

/** Unread counts for the tab badges, refreshed every 30 seconds. */
export function useUnread(on: boolean): [Unread, () => void] {
  const [u, setU] = useState<Unread>({ staff: 0, hr: 0, mine: 0 });
  const load = useCallback(() => { if (on) get<Unread>('/api/messages/summary').then(setU).catch(() => {}); }, [on]);
  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, [load]);
  return [u, load];
}

const cut = (t: string, n: number) => (t.length > n ? t.slice(0, n - 3).trim() + '…' : t);
/** The text an employee gets ("Also text … that they have a message"), as the server sends it. */
export function employeeText(store: string, by: string, body: string) {
  return `Wunderland ${store}: You have received a new message from ${by}:\n“${cut(body.trim(), 240)}”\nPlease log in to MyWunderland to reply: mywunderland.com\nReplies to this text aren’t read. Text STOP to opt out`;
}

function Bubbles({ items, onRetry }: { items: Item[]; onRetry?: (id: number) => void }) {
  return (
    <>
      {items.slice().reverse().map((m) => (
        <div key={m.id} className={'mb' + (m.mine ? ' mine' : '')}>
          <span className="mmeta">{m.who} · {m.at}</span>
          <div className="mtext">{m.body}</div>
          {m.status && (
            <span className={'msms' + (m.status.cls === 'bad' ? ' failed' : m.status.cls === 'ok' ? '' : ' none')}>{m.status.text}
              {m.status.retry && onRetry && <button className="btn2" onClick={() => onRetry(m.id)} style={{ minHeight: 26, padding: '1px 8px', fontSize: 12 }}>Retry text</button>}
            </span>
          )}
        </div>
      ))}
    </>
  );
}

// ======================================================================== desktop (managers, admins, HR)

interface Thread { employeeId: number; name: string; store: string; when: string; unread: number; snippet: string }
interface StaffThread { staffUserId: number; name: string; label: string; when: string; snippet: string; unread: number }
interface ThreadsResp { role: string; showStore: boolean; stores: { id: number; name: string }[]; threads: Thread[]; staffThreads: StaffThread[]; people: { id: number; name: string; store: string; block: string }[] }
interface ThreadResp {
  employee: { id: number; name: string; first: string; store: string; level: number | null; phone: string; block: string; archived: boolean };
  options: { key: string; label: string; unread: number }[]; conv: string; items: Item[]; route: string; canWrite: boolean; textFrom: string;
}
interface DeskResp { title: string; note: string; first: string; items: Item[] }

export function StaffMessages({ me, locationId, onSeen }: { me: Me; locationId: number; onSeen: () => void }) {
  const isHr = me.role === 'hr', isAdmin = me.role === 'admin';
  const [ch, setCh] = useState<'mgr' | 'hr'>('mgr');
  const [loc, setLoc] = useState<string>(isHr || isAdmin ? 'all' : String(locationId));
  useEffect(() => { if (!isHr && !isAdmin) setLoc(String(locationId)); }, [locationId, isHr, isAdmin]);
  const [list, setList] = useState<ThreadsResp | null>(null);
  const [sel, setSel] = useState<{ emp: number } | { staff: number } | null>(null);
  const [conv, setConv] = useState<string | undefined>(undefined);
  const [t, setT] = useState<ThreadResp | null>(null);
  const [desk, setDesk] = useState<DeskResp | null>(null);
  const [draft, setDraft] = useState('');
  const [alsoText, setAlsoText] = useState(true);
  const [busy, setBusy] = useState(false);
  const [group, setGroup] = useState(false);
  const [unread] = useUnread(true);
  const [toast, showToast] = useToast();

  const loadList = useCallback(async () => {
    try { setList(await get<ThreadsResp>(`/api/messages/threads?location=${loc}`)); } catch { setList(null); }
  }, [loc]);
  useEffect(() => { void loadList(); }, [loadList]);
  const loadThread = useCallback(async () => {
    if (ch === 'hr' && !isHr) { setDesk(await get<DeskResp>('/api/messages/hr-desk')); onSeen(); return; }
    if (!sel) { setT(null); setDesk(null); return; }
    if ('staff' in sel) { setT(null); setDesk(await get<DeskResp>(`/api/messages/hr-desk?staff=${sel.staff}`)); onSeen(); return; }
    setDesk(null);
    const r = await get<ThreadResp>(`/api/messages/thread?employee=${sel.emp}${conv ? `&conv=${conv}` : ''}`);
    setT(r); if (!conv) setConv(r.conv); onSeen();
  }, [sel, conv, ch, isHr, onSeen]);
  useEffect(() => { void loadThread().catch(() => showToast('Can’t load that conversation.')); }, [loadThread, showToast]);
  // new messages arrive while the page is open
  useEffect(() => { const x = setInterval(() => { void loadList(); void loadThread().catch(() => {}); }, 30_000); return () => clearInterval(x); }, [loadList, loadThread]);

  const open = (s: { emp: number } | { staff: number }) => { setSel(s); setConv(undefined); setDraft(''); setAlsoText(true); };
  const send = async () => {
    setBusy(true);
    try {
      if (ch === 'hr' && !isHr) await post('/api/messages/hr-desk/send', { body: draft });
      else if (sel && 'staff' in sel) await post('/api/messages/hr-desk/send', { staffUserId: sel.staff, body: draft });
      else if (sel && t) await post('/api/messages/staff/send', { employeeId: sel.emp, conv: conv ?? t.conv, body: draft, alsoText: alsoText && !t.employee.block });
      setDraft(''); setAlsoText(true); await loadThread(); await loadList();
    } catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t send. Try again.'); }
    finally { setBusy(false); }
  };
  const retry = async (id: number) => {
    try { const r = await post<{ ok: boolean }>('/api/messages/retry', { messageId: id }); if (!r.ok) showToast('The text didn’t go through again.'); await loadThread(); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t work.'); }
  };

  const hrMode = ch === 'hr' && !isHr;
  const intro = hrMode ? 'Write to HR privately. Only HR can read this conversation; other managers and admins can’t.'
    : isAdmin ? 'Every store’s conversations between managers and employees, private ones included. Only admins can see all of them.'
    : isHr ? 'HR’s own conversations with employees, at every store. Only HR can see these; store managers and admins can’t. When an employee replies, it comes only to HR.' : '';
  const storeName = list?.stores.find((s) => String(s.id) === loc)?.name;
  const groupLoc = loc === 'all' ? locationId : Number(loc);
  const first = t?.employee.first ?? '';

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Messages</h1>
      {!isHr && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} role="group" aria-label="Which conversations">
          <button className={'pill' + (ch === 'mgr' ? ' on' : '')} onClick={() => { setCh('mgr'); setDraft(''); }}>Employee conversations</button>
          <button className={'pill' + (ch === 'hr' ? ' on' : '')} onClick={() => { setCh('hr'); setDraft(''); }}>Message HR{unread.hr ? ` (${unread.hr})` : ''}</button>
        </div>
      )}
      {intro && <div style={{ fontSize: 14, color: '#4A5561' }}>{intro}</div>}
      <AlertPrefs me={me} />
      {hrMode ? (
        <div className="msgwrap solo">
          <div className="msgthread">
            <div className="msghead"><b>HR</b><span>Private: only HR can read this</span></div>
            <div className="msgscroll">{desk && <Bubbles items={desk.items} />}{desk && !desk.items.length && <div className="msgempty">No messages yet. Write the first one below.</div>}</div>
            <div className="msgcompose">
              <textarea className="inp" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Write to HR…" aria-label="Message" maxLength={1000} />
              <div className="msgactions"><button className="tbtn primary" disabled={!draft.trim() || busy} onClick={() => void send()}>Send message</button></div>
            </div>
          </div>
        </div>
      ) : (
        <div className="msgwrap">
          <div className="msgside">
            {(isAdmin || isHr) && list && (
              <select className="sel-sm" value={loc} onChange={(e) => { setLoc(e.target.value); setSel(null); }} aria-label="Which store's messages">
                <option value="all">All stores</option>{list.stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            <button className="tbtn" onClick={() => setGroup(true)} style={{ margin: '10px 10px 0', minHeight: 36 }}>Message several people…</button>
            <select className="sel-sm" value="" onChange={(e) => { if (e.target.value) open({ emp: Number(e.target.value) }); }} aria-label="Start a message to an employee">
              <option value="">New message to…</option>
              {(list?.people ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}{list?.showStore ? ` · ${p.store}` : ''}{p.block ? ' (can’t get texts)' : ''}</option>)}
            </select>
            <div className="msglist">
              {(list?.staffThreads ?? []).map((x) => (
                <button key={'s' + x.staffUserId} className={'mthread' + (sel && 'staff' in sel && sel.staff === x.staffUserId ? ' on' : '') + (x.unread ? ' unread' : '')}
                  onClick={() => open({ staff: x.staffUserId })} aria-label={`Conversation with ${x.name}${x.unread ? `, ${x.unread} unread` : ''}`}>
                  <b>{x.label}{x.unread > 0 && <span className="mbadge">{x.unread}</span>}</b><span className="mwhen">{x.when}</span><span className="msnip">{x.snippet}</span>
                </button>
              ))}
              {(list?.threads ?? []).map((x) => (
                <button key={x.employeeId} className={'mthread' + (sel && 'emp' in sel && sel.emp === x.employeeId ? ' on' : '') + (x.unread ? ' unread' : '')}
                  onClick={() => open({ emp: x.employeeId })} aria-label={`Conversation with ${x.name}${x.unread ? `, ${x.unread} unread` : ''}`}>
                  <b>{x.name}{list?.showStore ? ` · ${x.store}` : ''}{x.unread > 0 && <span className="mbadge">{x.unread}</span>}</b><span className="mwhen">{x.when}</span><span className="msnip">{x.snippet}</span>
                </button>
              ))}
              {list && !list.threads.length && !list.staffThreads.length && <div className="msgnone">No messages yet at {storeName ?? (loc === 'all' ? 'any store' : 'this store')}. Use “New message to…” above to write to someone.</div>}
            </div>
          </div>
          <div className="msgthread">
            {desk && sel && 'staff' in sel && <>
              <div className="msghead"><b>{desk.title}</b><span>{desk.note}</span></div>
              <div className="msgscroll"><Bubbles items={desk.items} /></div>
              <div className="msgcompose">
                <textarea className="inp" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={`Write to ${desk.first}…`} aria-label="Message" maxLength={1000} />
                <div className="msgactions"><button className="tbtn primary" disabled={!draft.trim() || busy} onClick={() => void send()}>Send message</button></div>
              </div>
            </>}
            {t && sel && 'emp' in sel && <>
              <div className="msghead">
                <b>{t.employee.name}</b>
                <span>{(isAdmin || isHr) ? `${t.employee.store} · ` : ''}Level {t.employee.level ?? 0} · {t.employee.phone || 'no mobile number'}</span>
                <span className="routenote">{t.route}</span>
                {t.options.length > 1 && (
                  <div className="convbtns" role="group" aria-label="Which conversation">
                    {t.options.map((o) => <button key={o.key} className={'pill' + (o.key === (conv ?? t.conv) ? ' on' : '')} onClick={() => { setConv(o.key); setDraft(''); }}>{o.label}{o.unread ? ` (${o.unread})` : ''}</button>)}
                  </div>
                )}
              </div>
              <div className="msgscroll">
                <Bubbles items={t.items} onRetry={(id) => void retry(id)} />
                {!t.items.length && <div className="msgempty">No messages yet. Write the first one below.</div>}
              </div>
              {t.canWrite && !t.employee.archived && (
                <div className="msgcompose">
                  <textarea className="inp" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={`Write to ${first}…`} aria-label="Message" maxLength={1000} />
                  <div className="msgactions">
                    <button className="tbtn primary" disabled={!draft.trim() || busy} onClick={() => void send()}>Send message</button>
                    <label className="allday" title="Sends a short text with your message and asks them to log in to MyWunderland to reply. Replies to the text aren’t read.">
                      <input type="checkbox" checked={alsoText && !t.employee.block} disabled={!!t.employee.block} onChange={(e) => setAlsoText(e.target.checked)} />Also text {first} that they have a message</label>
                    {t.employee.block && <span className="smsnote bad">No text possible · {t.employee.block}. They’ll see it when they next log in.</span>}
                  </div>
                  {alsoText && !t.employee.block && draft.trim() && <div className="msgprev">The text they’ll get:<div className="bubble">{employeeText(t.employee.store, t.textFrom, draft)}</div></div>}
                </div>
              )}
            </>}
            {!sel && <div className="msgempty">Pick a conversation on the left, or start a new message.</div>}
          </div>
        </div>
      )}
      {group && <GroupModal locationId={groupLoc} onClose={() => { setGroup(false); void loadList(); }} />}
      {toast}
    </section>
  );
}

// ------------------------------------------------------------------------ My text alerts
interface PrefsResp { phone: string; mode: 'mine' | 'all' | 'off'; quietOn: boolean; quietFrom: number; quietTo: number; quietNow: boolean; role: string; store: string; name: string; held: number; heldAt: string | null; digest: string | null; example: string }
const minLabel = (m: number) => { const h = Math.floor(m / 60), mm = m % 60; return `${h % 12 === 0 ? 12 : h % 12}:${mm < 10 ? '0' : ''}${mm} ${h >= 12 ? 'PM' : 'AM'}`; };
const short = (m: number) => minLabel(m).replace(':00', '');

function AlertPrefs({ me }: { me: Me }) {
  const [p, setP] = useState<PrefsResp | null>(null);
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(async () => { const r = await get<PrefsResp>('/api/messages/alerts'); setP(r); setPhone(r.phone); }, []);
  useEffect(() => { void load().catch(() => {}); }, [load]);
  if (!p) return null;
  const isHr = p.role === 'hr', isAdmin = p.role === 'admin';
  const save = async (patch: Record<string, unknown>) => {
    try { await put('/api/messages/alerts', patch); setErr(''); await load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t save.'); }
  };
  const on = p.mode !== 'off' && !!p.phone;
  const summary = (on && p.quietOn ? `Quiet ${short(p.quietFrom)}–${short(p.quietTo)} · ` : '')
    + (p.mode === 'off' ? 'Off' : !p.phone ? 'Off · no phone number yet'
      : isHr ? `Every message sent to HR · ${p.phone}`
      : p.mode === 'all' ? `${isAdmin ? 'Every message, all stores' : `Every ${p.store} message`} · ${p.phone}`
      : `${isAdmin ? 'Only HR’s replies to me' : `Messages to me or to all ${p.store} managers`} · ${p.phone}`);
  const modeOpts: [string, string][] = isHr ? [['all', 'Text me every message sent to HR'], ['off', 'Don’t text me']]
    : isAdmin ? [['mine', 'Text me only HR’s replies to me'], ['all', 'Text me every message from any store'], ['off', 'Don’t text me']]
    : [['mine', `Text me messages to me or to all ${p.store} managers`], ['off', 'Don’t text me']];
  const note = p.mode === 'off' ? (isHr ? 'You won’t get texts. HR messages still wait here.' : 'You won’t get texts. Messages still wait here for you.')
    : isHr ? 'When an employee writes to HR, it’s texted to you. Managers never see these.'
    : isAdmin ? (p.mode === 'all' ? 'You get a text for every message an employee writes, at every store, private ones included.' : 'You get a text when HR answers you. Employee messages still wait here.')
    : `When an employee writes to you privately or to all ${p.store} managers, it’s texted to you. Messages to another manager privately aren’t.`;
  const times = Array.from({ length: 48 }, (_, i) => i * 30);
  return (
    <div className="prefbox">
      <button className="prefhead" onClick={() => setOpen((x) => !x)} aria-expanded={open}><b>My text alerts</b><span>{summary}</span><span className="prefchev">{open ? '▴' : '▾'}</span></button>
      {open && (
        <div className="prefbody">
          <div style={{ fontSize: 13, color: '#4A5561' }}>For {p.name} only. Each manager sets their own.</div>
          <div className="formgrid">
            <label>My mobile number<input className="inp" value={phone} onChange={(e) => setPhone(e.target.value)} onBlur={() => { if (phone !== p.phone) void save({ phone }); }}
              placeholder="503-555-0100" inputMode="tel" aria-label="My mobile number for message alerts" /></label>
            <label>Text me when an employee writes<select className="sel-sm" value={p.mode} onChange={(e) => void save({ mode: e.target.value })} aria-label="When to text me" style={{ minHeight: 40 }}>
              {modeOpts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          </div>
          {err && <div className="fieldnote bad">{err}</div>}
          <div style={{ fontSize: 13, color: '#17212B' }}>{note}</div>
          <div className="bubble" style={{ maxWidth: 460, fontSize: 12, color: '#4A5561' }}>Example: {p.example}</div>
          <div className="dndrow">
            <label className="rulecb" style={{ fontSize: 14 }}><input type="checkbox" checked={p.quietOn} disabled={!on} onChange={(e) => void save({ quietOn: e.target.checked })} />Quiet hours (do not disturb)</label>
            {p.quietOn && (
              <span className="dndtimes"><span>from</span>
                <select className="sel-sm" value={p.quietFrom} onChange={(e) => void save({ quietFrom: Number(e.target.value) })} aria-label="Quiet hours start">{times.map((m) => <option key={m} value={m}>{minLabel(m)}</option>)}</select>
                <span>to</span>
                <select className="sel-sm" value={p.quietTo} onChange={(e) => void save({ quietTo: Number(e.target.value) })} aria-label="Quiet hours end">{times.map((m) => <option key={m} value={m}>{minLabel(m)}</option>)}</select>
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: '#4A5561' }}>{p.quietOn
            ? `No texts from ${minLabel(p.quietFrom)} to ${minLabel(p.quietTo)} (store time). Anything that comes in then is sent to you in one text at ${minLabel(p.quietTo)}.${p.quietNow ? ' Quiet hours are on right now.' : ''}`
            : 'Turn on to stop texts at night. Messages still arrive here right away.'}</div>
          {p.held > 0 && <>
            <div className="dndwait"><span>{p.held} message{p.held === 1 ? '' : 's'} waiting · will be texted to you at {p.heldAt}</span></div>
            <div className="bubble" style={{ maxWidth: 460, fontSize: 12, color: '#4A5561' }}>The text you’ll get: {p.digest}</div>
          </>}
        </div>
      )}
      {me.role === 'hr' && null}
    </div>
  );
}

// ------------------------------------------------------------------------ Message several people…
interface GroupPeople { store: string; from: string; hr: boolean; people: { id: number; name: string; level: number | null; phone: string; block: string; today: boolean; week: boolean }[] }

function GroupModal({ locationId, onClose }: { locationId: number; onClose: () => void }) {
  const [g, setG] = useState<GroupPeople | null>(null);
  const [picked, setPicked] = useState<Record<number, boolean>>({});
  const [text, setText] = useState('');
  const [alsoText, setAlsoText] = useState(true);
  const [done, setDone] = useState<{ results: Record<number, string>; count: number; texted: number; failed: number } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { get<GroupPeople>(`/api/messages/group-people?location=${locationId}`).then(setG).catch(() => setErr('Can’t load the people.')); }, [locationId]);
  const ids = (g?.people ?? []).filter((p) => picked[p.id]).map((p) => p.id);
  const texts = alsoText ? (g?.people ?? []).filter((p) => picked[p.id] && !p.block).length : 0;
  const pick = (f: (p: GroupPeople['people'][number]) => boolean) => setPicked(Object.fromEntries((g?.people ?? []).filter(f).map((p) => [p.id, true])));
  const send = () => setConfirm({
    title: `Send this message to ${ids.length} ${ids.length === 1 ? 'person' : 'people'}?`,
    text: (g?.hr ? 'This goes from HR. Each person gets it in their own private HR conversation, and replies come only to HR.' : 'Each person gets it in their own conversation, so their replies come back to you privately.')
      + (texts ? ` ${texts} of them also get a text right away. Texts can’t be taken back once sent.` : ' No texts will be sent.'),
    ok: 'Send', run: async () => {
      try { setDone(await post('/api/messages/group', { locationId, ids, body: text, alsoText })); }
      catch (e) { setErr(e instanceof ApiError ? e.message : 'That didn’t send.'); }
    },
  });
  return (
    <div className="modalbg" role="presentation"><div className="modal widemodal" role="dialog" aria-modal="true" aria-labelledby="grp-title">
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px' }}>
        <b id="grp-title" style={{ fontSize: 18 }}>{g?.hr ? 'HR message to several people' : 'Message several people'} · {g?.store ?? ''}</b><span style={{ flex: '1 1 auto' }} />
        {!done && (
          <span role="group" aria-label="Quick picks" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
            <button className="pill" onClick={() => pick(() => true)}>Everyone</button>
            <button className="pill" onClick={() => pick((p) => p.today)}>Working today</button>
            <button className="pill" onClick={() => pick((p) => p.week)}>Working this week</button>
            <button className="pill" onClick={() => pick((p) => (p.level ?? 0) >= 6)}>Leads &amp; managers (6–9)</button>
            <button className="pill" onClick={() => setPicked({})}>No one</button>
          </span>
        )}
      </div>
      {!done && <div style={{ fontSize: 13, color: '#4A5561' }}>Tick who should get it, or use a quick pick. Each person gets the message in their own conversation, so replies come back to you one by one and nobody sees anyone else’s answer.</div>}
      {done && <div className="sharebar" style={{ background: '#EEF7EE', borderColor: '#BDE8CD' }}>✓ Sent to {done.count} {done.count === 1 ? 'person' : 'people'} · {done.texted} texted{done.failed ? ` · ${done.failed} texts failed (open their conversation to retry)` : ''}</div>}
      {err && <div className="errbar">{err}</div>}
      <div className="smsgrid">
        <div className="smslist">
          {(g?.people ?? []).map((p) => {
            const st = done?.results[p.id];
            return (
              <label key={p.id} className="smsrow" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={!!picked[p.id]} disabled={!!done} onChange={() => setPicked((x) => ({ ...x, [p.id]: !x[p.id] }))} aria-label={`Include ${p.name}`} />
                <b style={{ minWidth: 130 }}>{p.name}</b><span style={{ fontSize: 12, color: '#4A5561', minWidth: 22 }}>L{p.level ?? 0}</span>
                <span className="mono" style={{ fontSize: 12, color: '#4A5561' }}>{p.phone || '—'}</span>
                <span className="smsnote bad">{p.block ? 'Can’t get texts · sees it in the app' : ''}</span><span style={{ flex: '1 1 auto' }} />
                <span className={'smsstat ' + (st === 'sent' ? 'delivered' : st === 'failed' ? 'failed' : '')}>{st == null ? '' : st === 'sent' ? 'Sent · texted' : st === 'failed' ? 'Sent · text failed' : 'Sent (app only)'}</span>
              </label>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea className="inp" value={text} onChange={(e) => setText(e.target.value)} disabled={!!done} placeholder="e.g. Staff meeting Sunday at 9 AM before we open." aria-label="Message to send" maxLength={1000} style={{ minHeight: 110, resize: 'vertical' }} />
          <label className="allday"><input type="checkbox" checked={alsoText} disabled={!!done} onChange={(e) => setAlsoText(e.target.checked)} />Also text them that they have a message</label>
          <div style={{ fontSize: 13, color: '#17212B', fontWeight: 600 }}>{ids.length} picked{alsoText ? ` · ${texts} will get a text, ${ids.length - texts} in the app only` : ''}</div>
          {alsoText && text.trim() && g && <div className="msgprev">The text they’ll get:<div className="bubble">{employeeText(g.store, g.from, text)}</div></div>}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>Close</button>
        {!done && <button className="tbtn primary" disabled={!ids.length || !text.trim()} onClick={send} style={{ minWidth: 150 }}>Send to {ids.length} {ids.length === 1 ? 'person' : 'people'}…</button>}
      </div>
      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
    </div></div>
  );
}

// ======================================================================== employee phone tab

interface MeResp { store: string; first: string; to: string; options: { key: string; label: string; unread: number }[]; items: Item[] }

export function EmployeeMessages({ draft: initial, onSeen }: { draft?: { text: string } | null; onSeen: () => void }) {
  const [to, setTo] = useState<string | undefined>(undefined);
  const [d, setD] = useState<MeResp | null>(null);
  const [draft, setDraft] = useState(initial?.text ?? '');
  const [busy, setBusy] = useState(false);
  const [toast, showToast] = useToast();
  const load = useCallback(async () => {
    const r = await get<MeResp>(`/api/messages/me${to ? `?to=${to}` : ''}`);
    setD(r); if (!to) setTo(r.to); onSeen();
  }, [to, onSeen]);
  useEffect(() => { void load().catch(() => showToast('Can’t load your messages.')); }, [load, showToast]);
  useEffect(() => { const x = setInterval(() => { void load().catch(() => {}); }, 30_000); return () => clearInterval(x); }, [load]);
  if (!d) return null;
  const hr = (to ?? d.to) === 'hr';
  const mgrOpts = d.options.filter((o) => o.key !== 'hr');
  const mgrUnread = mgrOpts.reduce((n, o) => n + o.unread, 0), hrUnread = d.options.find((o) => o.key === 'hr')?.unread ?? 0;
  const cur = d.options.find((o) => o.key === (to ?? d.to));
  const send = async () => {
    setBusy(true);
    try { await post('/api/messages/me/send', { to: to ?? d.to, body: draft }); setDraft(''); await load(); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t send. Try again.'); }
    finally { setBusy(false); }
  };
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Messages</h1>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} role="group" aria-label="Which conversations">
        <button className={'pill' + (!hr ? ' on' : '')} onClick={() => { if (hr) setTo(undefined); }}>My managers{mgrUnread ? ` (${mgrUnread})` : ''}</button>
        <button className={'pill' + (hr ? ' on' : '')} onClick={() => setTo('hr')}>HR{hrUnread ? ` (${hrUnread})` : ''}</button>
      </div>
      <div style={{ fontSize: 14, color: '#4A5561' }}>{hr ? 'Write to HR privately. Only HR can read this conversation; your managers can’t see it.'
        : `Pick who to write to: one of your ${d.store} managers, or all of them. They’ll reply here, and you’ll see it the next time you log in.`}</div>
      <div className="msgwrap solo">
        <div className="msgthread">
          <div className="msghead"><b>{hr ? 'HR' : `${d.store} managers`}</b>{hr && <span>Private: only HR can read this</span>}</div>
          <div className="msgscroll">
            <Bubbles items={d.items} />
            {!d.items.length && <div className="msgempty">No messages yet. Write the first one below.</div>}
          </div>
          <div className="msgcompose">
            {!hr && (
              <label className="msgto">To<select className="sel-sm" value={to ?? d.to} onChange={(e) => setTo(e.target.value)} aria-label="Who the message is for">
                {mgrOpts.map((o) => <option key={o.key} value={o.key}>{o.label}{o.unread ? ` · ${o.unread} new` : ''}</option>)}</select></label>
            )}
            <textarea className="inp" value={draft} onChange={(e) => setDraft(e.target.value)} aria-label="Message" maxLength={1000}
              placeholder={hr ? 'Write to HR…' : cur && cur.key !== 'all' ? `Write to ${cur.label.split(' ')[0]}…` : 'Write to your managers…'} />
            <div className="msgactions"><button className="tbtn primary" disabled={!draft.trim() || busy} onClick={() => void send()}>Send message</button></div>
          </div>
        </div>
      </div>
      {toast}
    </section>
  );
}

// ======================================================================== phone Store tab (managers, admins, HR)

/** "Messages from employees" (or "Messages to HR") card: the 8 latest conversations; tap one to open it. */
export function StoreMessagesCard({ locationId, title, onOpen }: { locationId: number | 'all'; title: string; onOpen: (employeeId: number) => void }) {
  const [list, setList] = useState<ThreadsResp | null>(null);
  const load = useCallback(async () => { try { setList(await get<ThreadsResp>(`/api/messages/threads?location=${locationId}`)); } catch { setList(null); } }, [locationId]);
  useEffect(() => { void load(); const x = setInterval(() => void load(), 30_000); return () => clearInterval(x); }, [load]);
  const threads = (list?.threads ?? []).slice(0, 8);
  return (
    <div className="stcard"><span className="tlabel">{title}</span>
      {list && !threads.length && <span className="stnone">No messages yet</span>}
      {threads.map((m) => (
        <button key={m.employeeId} className={'stmsg' + (m.unread ? ' unread' : '')} onClick={() => onOpen(m.employeeId)}
          aria-label={`Conversation with ${m.name}${m.unread ? `, ${m.unread} unread` : ''}`}>
          <span className="stmrow"><b>{m.name}{list?.showStore ? ` · ${m.store}` : ''}</b>{m.unread > 0 && <span className="mbadge">{m.unread}</span>}<span className="stmwhen">{m.when}</span></span>
          <span className="stmtext">{m.snippet}</span>
        </button>
      ))}
    </div>
  );
}

/** One conversation opened from the Store tab, with a reply box (same privacy rules as the desktop). */
export function PhoneThread({ employeeId, onBack }: { employeeId: number; onBack: () => void }) {
  const [conv, setConv] = useState<string | undefined>(undefined);
  const [t, setT] = useState<ThreadResp | null>(null);
  const [draft, setDraft] = useState('');
  const [alsoText, setAlsoText] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, showToast] = useToast();
  const load = useCallback(async () => {
    const r = await get<ThreadResp>(`/api/messages/thread?employee=${employeeId}${conv ? `&conv=${conv}` : ''}`);
    setT(r); if (!conv) setConv(r.conv);
  }, [employeeId, conv]);
  useEffect(() => { void load().catch(() => showToast('Can’t load that conversation.')); }, [load, showToast]);
  useEffect(() => { const x = setInterval(() => void load().catch(() => {}), 30_000); return () => clearInterval(x); }, [load]);
  const send = async () => {
    if (!t) return;
    setBusy(true);
    try { await post('/api/messages/staff/send', { employeeId, conv: conv ?? t.conv, body: draft, alsoText: alsoText && !t.employee.block }); setDraft(''); setAlsoText(true); await load(); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t send. Try again.'); }
    finally { setBusy(false); }
  };
  const first = t?.employee.first ?? '';
  return (
    <section className="estore stconv">
      <button className="elink" onClick={onBack} style={{ alignSelf: 'flex-start', paddingLeft: 0 }}>‹ Back to the Store tab</button>
      {t && <>
        <div className="esthead"><h1>{t.employee.name}</h1></div>
        <div className="estsub">{t.employee.store} · level {t.employee.level ?? 0}</div>
        {t.options.length > 1 && (
          <div className="convbtns" role="group" aria-label="Which conversation">
            {t.options.map((o) => <button key={o.key} className={'pill' + (o.key === (conv ?? t.conv) ? ' on' : '')} onClick={() => { setConv(o.key); setDraft(''); }}>{o.label}{o.unread ? ` (${o.unread})` : ''}</button>)}
          </div>
        )}
        {t.route && <div className="estsub">{t.route}</div>}
        <div className="stmsgs" style={{ flexDirection: 'column-reverse' }}><Bubbles items={t.items} />{!t.items.length && <div className="stnone">No messages in this conversation yet.</div>}</div>
        {t.canWrite && !t.employee.archived && <>
          <textarea className="inp" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={`Reply to ${first}…`} aria-label="Reply" maxLength={1000} style={{ minHeight: 90 }} />
          {!t.employee.block
            ? <label className="allday" style={{ fontSize: 13 }}><input type="checkbox" checked={alsoText} onChange={(e) => setAlsoText(e.target.checked)} />Also text {first} that they have a message</label>
            : <span className="stnone">No text possible · {t.employee.block}</span>}
          <button className="ebig" onClick={() => void send()} disabled={!draft.trim() || busy}>Send reply</button>
        </>}
      </>}
      {toast}
    </section>
  );
}
